/**
 * Lasten tipiziran store (T1 §5). Brez knjižnice — površina stanja je
 * ~10 ploskih polj in avtomatsko sledenje odvisnostim tu nima česa rešiti.
 *
 * Konvencija renderiranja, ki jo store predpostavlja:
 * vsaka komponenta ima svoje poddrevo DOM in eno samo metodo update(state).
 * Nobena komponenta ne popravlja vozlišč druge.
 */

type Listener<T> = (state: T) => void;

export interface Store<T extends object> {
  get(): T;
  set(patch: Partial<T>): void;
  subscribe(listener: Listener<T>): () => void;
}

function shallowEqual<T extends object>(a: T, b: T): boolean {
  const keys = Object.keys(a) as (keyof T)[];
  if (keys.length !== Object.keys(b).length) return false;
  return keys.every((k) => Object.is(a[k], b[k]));
}

export function createStore<T extends object>(initial: T): Store<T> {
  let state = initial;
  const listeners = new Set<Listener<T>>();

  return {
    get: () => state,
    set(patch) {
      const next = { ...state, ...patch };
      if (shallowEqual(next, state)) return;
      state = next;
      for (const listener of listeners) listener(state);
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
}
